/* eslint-disable */
// => https://gitlab.com/gitlab-org/gitlab-ui/-/blob/main/src/components/base/filtered_search/filtered_search.vue
// Copyright GitLab B.V.
//
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
import { GlToken } from '@gitlab/ui';
import { ApckGlFilteredSearchTokenSegment } from './apck_glfiltered_search_token_segment';
import {
  INTENT_ACTIVATE_PREVIOUS,
  TERM_TOKEN_TYPE,
  TOKEN_CLOSE_SELECTOR,
  match,
  tokenToOption,
  termTokenDefinition,
  stopEvent,
} from './filtered_search_utils';
import { ApckGlFilteredSearchSuggestion } from './apck_glfiltered_search_suggestion';

export const ApckGlFilteredSearchTerm = {
  name: 'ApckGlFilteredSearchTerm',
  components: {
    'gl-filtered-search-token-segment': ApckGlFilteredSearchTokenSegment,
    'gl-filtered-search-suggestion': ApckGlFilteredSearchSuggestion,
    GlToken,
  },
  inject: ['termsAsTokens'],
  inheritAttrs: false,
  props: {
    /**
     * Tokens available for this filtered search instance.
     */
    availableTokens: {
      type: Array,
      required: true,
    },
    /**
     * Determines if the term is being edited or not.
     */
    active: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Current term value.
     */
    value: {
      type: Object,
      required: false,
      default: () => ({ data: '' }),
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * HTML attributes to add to the search input.
     */
    searchInputAttributes: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * If this is the last token.
     */
    isLastToken: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The current `value` (tokens) of the ancestor GlFilteredSearch component.
     */
    currentValue: {
      type: Array,
      required: false,
      default: () => [],
    },
    cursorPosition: {
      type: String,
      required: false,
      default: 'end',
      validator: (value) => ['start', 'end'].includes(value),
    },
    /**
     * The title of the text search option. Ignored unless termsAsTokens is enabled.
     */
    searchTextOptionLabel: {
      type: String,
      required: false,
      default: termTokenDefinition.title,
    },
    viewOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    showInput() {
      return this.termsAsTokens() || Boolean(this.placeholder);
    },
    showToken() {
      return this.termsAsTokens() && Boolean(this.value.data);
    },
    suggestedTokens() {
      const tokens = this.availableTokens.filter((token) => match(token.title, this.value.data));
      if (this.termsAsTokens() && this.value.data) {
        tokens.push({
          ...termTokenDefinition,
          title: this.searchTextOptionLabel,
        });
      }

      return tokens.map(tokenToOption);
    },
    internalValue: {
      get() {
        return this.value.data;
      },
      set(data) {
        /**
         * Emitted when the token changes its value.
         *
         * @event input
         * @type {object} dataObj Object containing the update value.
         */
        this.$emit('input', { data });
      },
    },
    eventListeners() {
      return this.viewOnly ? {} : { mousedown: this.destroyByClose };
    },
  },
  methods: {
    onBackspace() {
      /**
       * Emitted when token value is empty and backspace is pressed.
       * Includes user intent to activate previous token.
       *
       * @event destroy
       * @type {object} details The user intent
       */
      this.$emit('destroy', { intent: INTENT_ACTIVATE_PREVIOUS });
    },
    destroyByClose(event) {
      if (event.target.closest(TOKEN_CLOSE_SELECTOR)) {
        stopEvent(event);
        this.$emit('destroy');
      }
    },
    onComplete(type) {
      if (type === TERM_TOKEN_TYPE) {
        // We've completed this term token
        this.$emit('complete');
      } else {
        // We're changing the current token type
        this.$emit('replace', { type });
      }
    },
  },
  template: `
  <div class="gl-h-auto gl-filtered-search-term" data-testid="filtered-search-term">
    <!--
      Emitted when this term token is clicked.
      @event activate
    -->

    <!--
      Emitted when this term token will lose its focus.
      @event deactivate
    -->

    <!--
      Emitted when autocomplete entry is selected.
      @event replace
      @property {object} token Replacement token configuration.
    -->

    <!--
      Emitted when the token is submitted.
      @event submit
    -->

    <!--
      Emitted when Space is pressed in-between term text.
      Not emitted when termsAsTokens is true.
      @event split
      @property {array} newTokens Token configurations
    -->

    <gl-filtered-search-token-segment
      ref="segment"
      v-model="internalValue"
      is-term
      class="gl-filtered-search-term-token"
      :active="active"
      :cursor-position="cursorPosition"
      :search-input-attributes="searchInputAttributes"
      :is-last-token="isLastToken"
      :current-value="currentValue"
      :view-only="viewOnly"
      :options="suggestedTokens"
      @activate="$emit('activate')"
      @deactivate="$emit('deactivate')"
      @complete="onComplete"
      @backspace="onBackspace"
      @submit="$emit('submit')"
      @split="$emit('split', $event)"
      @previous="$emit('previous')"
      @next="$emit('next')"
    >
      <template #view>
        <gl-token
          v-if="showToken"
          :class="{ 'gl-cursor-pointer': !viewOnly }"
          :view-only="viewOnly"
          v-on="eventListeners"
          >{{ value.data }}</gl-token
        >

        <input
          v-else-if="showInput"
          v-bind="searchInputAttributes"
          class="gl-filtered-search-term-input"
          :class="{ 'gl-bg-gray-10': viewOnly }"
          :placeholder="placeholder"
          :aria-label="placeholder"
          :readonly="viewOnly"
          data-testid="filtered-search-term-input"
          @focusin="$emit('activate')"
          @focusout="$emit('deactivate')"
        />

        <template v-else>{{ value.data }}</template>
      </template>
    </gl-filtered-search-token-segment>
  </div>
  `,
};
